import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const PayrollApproval = () => {
    const [payrolls, setPayrolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('month_year');

    const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
    const [monthFilter, setMonthFilter] = useState('');

    // User info
    const [currentUser, setCurrentUser] = useState(null);

    // Additional data for display
    const [units, setUnits] = useState([]);

    // User permissions state
    const [userPermissions, setUserPermissions] = useState({
        can_approve: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch current user info
    const fetchCurrentUser = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return null;
            }

            const response = await axios.get(`${API_BASE}payroll/get_current_user.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.user) {
                const user = response.data.user;
                setCurrentUser(user);

                // Check if user is Super Admin/Admin
                const userRole = user.role_name;
                const isSuperAdmin = userRole === 'Super Admin' || userRole === 'Admin';

                setUserPermissions({
                    can_approve: isSuperAdmin,
                    loaded: true
                });
                return user;
            } else {
                setError(response.data.error || 'Failed to fetch user info');
                return null;
            }
        } catch (error) {
            console.error('Error fetching current user:', error);
            setError('Failed to fetch user information');
            return null;
        }
    }, [API_BASE]);

    // Fetch processed payrolls
    const fetchProcessedPayrolls = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}payroll/get_processed_payrolls.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    year: yearFilter,
                    month: monthFilter,
                    search: searchTerm
                },
                timeout: 10000
            });

            if (response.data.success) {
                setPayrolls(response.data.payrolls || []);
            } else {
                setPayrolls([]);
                setError(response.data.error || 'Failed to fetch processed payrolls');
            }
        } catch (error) {
            setPayrolls([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch processed payrolls';
            setError(errorMsg);
            console.error('Error fetching processed payrolls:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, yearFilter, monthFilter, searchTerm]);

    // Fetch units for the user's company
    const fetchUnits = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`${API_BASE}/get_units.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setUnits(response.data.units || []);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    }, [API_BASE]);

    useEffect(() => {
        const initializeData = async () => {
            const user = await fetchCurrentUser();
            if (user) {
                await Promise.all([
                    fetchUnits(),
                    fetchProcessedPayrolls()
                ]);
            }
        };

        initializeData();
    }, [fetchCurrentUser, fetchUnits, fetchProcessedPayrolls]);

    // Handle approve action
    const handleApprove = async (payrollId) => {
        if (!userPermissions.can_approve) {
            alert('You do not have permission to approve payrolls');
            return;
        }

        if (!window.confirm('Are you sure you want to approve this payroll? This will close the payroll period.')) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}payroll/approve_payroll.php`,
                { payroll_id: payrollId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                alert('Payroll approved and closed successfully');
                await fetchProcessedPayrolls(); // Refresh list
            } else {
                alert(response.data.error || 'Failed to approve payroll');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to approve payroll';
            alert(errorMsg);
            console.error('Error approving payroll:', error);
        }
    };

    // Handle reverse action
    const handleReverse = async (payrollId) => {
        if (!userPermissions.can_approve) {
            alert('You do not have permission to reverse payrolls');
            return;
        }

        if (!window.confirm('Are you sure you want to reverse this payroll? This will reopen the payroll period.')) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}payroll/reverse_payroll.php`,
                { payroll_id: payrollId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                alert('Payroll reversed and reopened successfully');
                await fetchProcessedPayrolls(); // Refresh list
            } else {
                alert(response.data.error || 'Failed to reverse payroll');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to reverse payroll';
            alert(errorMsg);
            console.error('Error reversing payroll:', error);
        }
    };

    // Get status badge styling
    const getStatusBadge = (status) => {
        const badgeClasses = {
            'Open': 'bg-warning text-dark',
            'Processed': 'bg-info text-white',
            'Closed': 'bg-success text-white'
        };
        return badgeClasses[status] || 'bg-secondary text-white';
    };

    // Sort payrolls
    const sortedPayrolls = [...payrolls].sort((a, b) => {
        switch (sortBy) {
            case 'month_year':
                return b.month_year.localeCompare(a.month_year);
            case 'from_date':
                return new Date(b.from_date) - new Date(a.from_date);
            case 'processed_on':
                return new Date(b.processed_on) - new Date(a.processed_on);
            default:
                return b.month_year.localeCompare(a.month_year);
        }
    });

    // Generate year options for filter
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    // Generate month options for filter
    const monthOptions = [
        { value: '', label: 'All Months' },
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    // Show loading
    if (loading || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading payroll approvals...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If user doesn't have permission, show access denied
    if (!userPermissions.can_approve) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">Only Admin/Super Admin users can approve payrolls.</p>
                                <p className="text-muted small">Your role: {currentUser?.role_name || 'Unknown'}</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/payroll')}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    Back to Payroll
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1 className="h3 mb-2">Payroll Approval</h1>
                            <p className="text-muted mb-0">
                                {currentUser?.company_name ?
                                    `Review and approve processed payrolls for ${currentUser.company_name}` :
                                    'Review and approve processed payrolls'}
                            </p>
                        </div>
                        <div>
                            <button
                                className="btn btn-secondary me-2"
                                onClick={() => navigate('/payroll')}
                            >
                                <i className="bi bi-arrow-left me-1"></i>
                                Back to Payroll
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={fetchProcessedPayrolls}
                                disabled={loading}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchProcessedPayrolls}
                                        disabled={loading}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filter Bar */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search payrolls..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    fetchProcessedPayrolls();
                                                }
                                            }}
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={fetchProcessedPayrolls}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                            ) : (
                                                'Search'
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={yearFilter}
                                        onChange={(e) => setYearFilter(e.target.value)}
                                    >
                                        {yearOptions.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={monthFilter}
                                        onChange={(e) => setMonthFilter(e.target.value)}
                                    >
                                        {monthOptions.map(month => (
                                            <option key={month.value} value={month.value}>
                                                {month.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="month_year">Sort by Month-Year</option>
                                        <option value="from_date">Sort by From Date</option>
                                        <option value="processed_on">Sort by Processed Date</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payrolls List */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-0">
                            {sortedPayrolls.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-check-circle display-4 text-muted"></i>
                                    <p className="mt-3 text-muted">
                                        {loading ? 'Loading...' : 'No processed payrolls found for approval'}
                                    </p>
                                    {!loading && (
                                        <button
                                            className="btn btn-primary mt-2"
                                            onClick={fetchProcessedPayrolls}
                                        >
                                            <i className="bi bi-arrow-clockwise me-1"></i>
                                            Refresh
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Month-Year</th>
                                                <th>Unit</th>
                                                <th>Period</th>
                                                <th>Status</th>
                                                <th>Processed By</th>
                                                <th>Processed On</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedPayrolls.map((payroll, index) => {
                                                const unit = units.find(u => u.unit_id === payroll.unit_id);

                                                return (
                                                    <tr key={payroll.payroll_id}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <strong>{payroll.month_year}</strong>
                                                        </td>
                                                        <td>{unit?.unit_name || payroll.unit_name || 'N/A'}</td>
                                                        <td>
                                                            {payroll.from_date} to {payroll.to_date}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${getStatusBadge(payroll.status)}`}>
                                                                {payroll.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {payroll.processed_by_name || 'N/A'}
                                                        </td>
                                                        <td>
                                                            {payroll.processed_on ?
                                                                new Date(payroll.processed_on).toLocaleString() :
                                                                'N/A'}
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <button
                                                                    className="btn btn-success"
                                                                    onClick={() => handleApprove(payroll.payroll_id)}
                                                                    title="Approve and Close Payroll"
                                                                    disabled={payroll.status === 'Closed'}
                                                                >
                                                                    <i className="bi bi-check-circle me-1"></i>
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    className="btn btn-warning"
                                                                    onClick={() => handleReverse(payroll.payroll_id)}
                                                                    title="Reverse to Open Status"
                                                                    disabled={payroll.status === 'Closed'}
                                                                >
                                                                    <i className="bi bi-arrow-counterclockwise me-1"></i>
                                                                    Reverse
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Footer */}
            {sortedPayrolls.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body">
                                <div className="row text-center">
                                    <div className="col-md-4">
                                        <h5 className="text-muted mb-1">Total Processed</h5>
                                        <h3 className="text-primary">{sortedPayrolls.length}</h3>
                                    </div>
                                    <div className="col-md-4">
                                        <h5 className="text-muted mb-1">Ready for Approval</h5>
                                        <h3 className="text-info">
                                            {sortedPayrolls.filter(p => p.status === 'Processed').length}
                                        </h3>
                                    </div>
                                    <div className="col-md-4">
                                        <h5 className="text-muted mb-1">Already Closed</h5>
                                        <h3 className="text-success">
                                            {sortedPayrolls.filter(p => p.status === 'Closed').length}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PayrollApproval;